import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { create_qr_design, get_all_qr_designs } from "../network/api";
import logo from "../assets/logo.png";
import { Plus, Save, Palette, Layout, Eye, Trash2 } from "lucide-react";

export default function Qr_code_designs() {
  const [designs, setDesigns] = useState<any[]>([]);
  const [form, setForm] = useState({
    name: "",
    qrColor: "#10B981",
    bgColor: "#FFFFFF",
    scanTextColor: "#10B981",
    shapeColor: "#D1FAE5",
    template: "circle",
  });
  const [showPreview, setShowPreview] = useState(true);

  const fetchDesigns = async () => {
    const data = await get_all_qr_designs();
    setDesigns(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await create_qr_design(form);
    setForm({
      name: "",
      qrColor: "#10B981",
      bgColor: "#FFFFFF",
      scanTextColor: "#10B981",
      shapeColor: "#D1FAE5",
      template: "circle",
    });
    fetchDesigns();
  };

  useEffect(() => {
    fetchDesigns();
  }, []);

  const renderTemplate = (qr: JSX.Element, template: string, colors: any) => {
    const { shapeColor, scanTextColor } = colors;

    switch (template) {
      case "circle":
        return (
          <div className="flex flex-col items-center">
            <div
              className="w-44 h-44 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: shapeColor }}
            >
              {qr}
            </div>
            <span
              className="mt-3 text-xs font-bold tracking-wider uppercase"
              style={{ color: scanTextColor }}
            >
              SCAN ME!
            </span>
          </div>
        );
      case "speech":
        return (
          <div
            className="p-4 rounded-xl relative flex flex-col items-center transition-all duration-300 hover:scale-105"
            style={{ backgroundColor: shapeColor }}
          >
            <div
              className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-4 h-4 rotate-45"
              style={{ backgroundColor: shapeColor }}
            />
            {qr}
            <span
              className="mt-2 text-xs font-bold tracking-wider uppercase"
              style={{ color: scanTextColor }}
            >
              SCAN ME!
            </span>
          </div>
        );
      case "banner":
        return (
          <div className="flex flex-col items-center">
            {qr}
            <div
              className="px-6 py-1.5 rounded-b-lg mt-2 text-xs font-bold tracking-wider uppercase transition-all duration-300"
              style={{ backgroundColor: shapeColor, color: scanTextColor }}
            >
              SCAN ME!
            </div>
          </div>
        );
      case "badge":
        return (
          <div className="flex flex-col items-center">
            <div
              className="p-4 rounded-full transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: shapeColor }}
            >
              {qr}
            </div>
            <span
              className="mt-2 text-xs font-bold tracking-wider uppercase"
              style={{ color: scanTextColor }}
            >
              SCAN ME!
            </span>
          </div>
        );
      case "rounded":
        return (
          <div
            className="p-4 rounded-2xl flex flex-col items-center transition-all duration-300 hover:scale-105"
            style={{ backgroundColor: shapeColor }}
          >
            {qr}
            <span
              className="mt-2 text-xs font-bold tracking-wider uppercase"
              style={{ color: scanTextColor }}
            >
              SCAN ME!
            </span>
          </div>
        );
      case "rectangle":
        return (
          <div
            className="p-4 flex flex-col items-center border-2 rounded-lg transition-all duration-300 hover:scale-105"
            style={{ borderColor: shapeColor }}
          >
            {qr}
            <span
              className="mt-2 text-xs font-bold tracking-wider uppercase"
              style={{ color: scanTextColor }}
            >
              SCAN ME!
            </span>
          </div>
        );
      default:
        return qr;
    }
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header - Green Theme */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 shadow-lg">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">QR Code Designs</h1>
            <p className="text-green-100 mt-1 text-sm">
              Create and manage custom QR code designs
            </p>
          </div>
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/20">
            <Layout className="w-5 h-5 text-white" />
            <span className="text-sm text-white font-medium">
              {designs.length} Designs
            </span>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center gap-2">
          <Palette className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Create New Design
          </h3>
        </div>

        <form className="p-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Design Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Design Name
              </label>
              <input
                type="text"
                placeholder="Enter design name..."
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                required
              />
            </div>

            {/* Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template
              </label>
              <select
                value={form.template}
                onChange={(e) => setForm({ ...form, template: e.target.value })}
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              >
                <option value="circle">Circle</option>
                <option value="speech">Speech Bubble</option>
                <option value="banner">Banner</option>
                <option value="badge">Badge</option>
                <option value="rounded">Rounded Box</option>
                <option value="rectangle">Rectangle</option>
              </select>
            </div>

            {/* Colors */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Color Customization
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  {
                    key: "qrColor",
                    label: "QR Foreground",
                    default: "#10B981",
                  },
                  {
                    key: "bgColor",
                    label: "QR Background",
                    default: "#FFFFFF",
                  },
                  {
                    key: "shapeColor",
                    label: "Shape Color",
                    default: "#D1FAE5",
                  },
                  {
                    key: "scanTextColor",
                    label: "Text Color",
                    default: "#10B981",
                  },
                ].map((c) => (
                  <div key={c.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {c.label}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={(form as any)[c.key]}
                        onChange={(e) =>
                          setForm({ ...form, [c.key]: e.target.value })
                        }
                        className="w-10 h-10 cursor-pointer rounded-lg border-2 border-gray-200 hover:border-green-500 transition"
                      />
                      <span className="font-mono text-xs text-gray-500">
                        {(form as any)[c.key]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-green-600" />
                <h4 className="text-sm font-semibold text-gray-700">
                  Live Preview
                </h4>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 flex justify-center border-2 border-dashed border-gray-200">
                {renderTemplate(
                  <QRCodeSVG
                    value={form.name || "Whatbox QR"}
                    size={130}
                    fgColor={form.qrColor}
                    bgColor={form.bgColor}
                    imageSettings={{
                      src: logo,
                      height: 32,
                      width: 32,
                      excavate: true,
                    }}
                  />,
                  form.template,
                  form
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="md:col-span-2">
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl transition font-medium shadow-sm"
              >
                <Save className="w-5 h-5" />
                Save Design
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Saved Designs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Layout className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Saved Designs
            </h3>
          </div>
          <span className="text-sm text-gray-500">
            {designs.length} designs
          </span>
        </div>

        {designs.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="flex flex-col items-center gap-3">
              <Layout className="w-16 h-16 text-gray-300" />
              <p className="text-gray-500">No designs created yet</p>
              <p className="text-sm text-gray-400">
                Create your first QR design above
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {designs.map((d) => (
              <div
                key={d._id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition duration-200"
              >
                <div className="flex justify-center p-4 bg-gray-50 rounded-xl">
                  {renderTemplate(
                    <QRCodeSVG
                      value={d.name}
                      size={110}
                      fgColor={d.qrColor}
                      bgColor={d.bgColor}
                      imageSettings={{
                        src: logo,
                        height: 28,
                        width: 28,
                        excavate: true,
                      }}
                    />,
                    d.template,
                    d
                  )}
                </div>
                <div className="mt-4">
                  <p className="font-medium text-gray-900 text-sm text-center">
                    {d.name}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2 text-xs text-gray-500">
                    <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                      {d.template}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
